import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, rem, SCREEN_BOTTOM_PADDING, SettingsItem, SettingsItemNavigate, SettingsSection, Spacer, withAnimatedNavigationHeader, } from 'design-system';
import { SettingsItemRightContent } from 'design-system/SettingsItem';
import { IconArrow } from 'design-system/icons/IconArrow';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useEnsureData from 'hooks/useEnsureData';
import { useAppSelector } from 'store/hooks';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { isWeb } from '../../../constants';
import DisputeWebView from '../components/DisputeWebView';
import useCreditReportDispute from '../hooks/useCreditReportDispute';
import { selectCreditReport, selectHasCreditReport } from '../selectors';
import useGetCreditReport from '../hooks/useGetCreditReport';
import { buildDisputeItems } from '../utils/disputeItems';
const strings = {
    headerTitle: 'Submit a dispute',
    headerSubtitle: `If you believe something is incorrect on your credit report, let us know and we'll pass it on to TransUnion. Disputes typically take up to 28 days to investigate.`,
    itemSectionName: 'Item to dispute',
    choosePlaceholder: 'Choose…',
};
const CreditReportDisputeScreen = withAnimatedNavigationHeader(({ scrollHandler, route, navigation }) => {
    const { category, searchReference } = route.params;
    const parsedAccountId = Number(route.params.accountId);
    const accountId = Number.isFinite(parsedAccountId)
        ? parsedAccountId
        : undefined;
    const data = useAppSelector(selectCreditReport);
    const fetchCreditReport = useGetCreditReport();
    const hasReport = useEnsureData(selectHasCreditReport, fetchCreditReport);
    const items = useMemo(() => buildDisputeItems(data, { category, accountId, searchReference }), [data, category, accountId, searchReference]);
    const hasItems = items.length > 0;
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, [navigation]);
    const { disputeItem, selectedMeta, isFetching, hasError, formFields, html, isPickerOpen, isSingleItem, onPressPicker, onDisputeError, isWebViewReady, onWebViewReady, } = useCreditReportDispute(items);
    // Once the report has loaded, if there's still nothing to dispute
    // (invalid params, or the item is no longer in the report), there's
    // nothing to show — send the user to the credit report home.
    useEffect(() => {
        if (hasReport && !hasItems) {
            navigation.navigate('CreditReportAccountsScreen');
        }
    }, [hasReport, hasItems, navigation]);
    if (!hasItems)
        return null;
    const summaryTitle = disputeItem?.description ??
        disputeItem?.label ??
        strings.choosePlaceholder;
    const renderSummary = () => {
        if (disputeItem?.rightLabel) {
            return (<SettingsItem title={summaryTitle} icon={selectedMeta?.icon} renderIcon={selectedMeta?.renderIcon} disabled={isSingleItem} onPress={onPressPicker}>
              <SettingsItemRightContent>
                <Text Numbers-16>{disputeItem.rightLabel}</Text>
                {!isSingleItem ? (<>
                    <Spacer w={8}/>
                    <IconArrow direction={isPickerOpen ? 'up' : 'down'}/>
                  </>) : null}
              </SettingsItemRightContent>
            </SettingsItem>);
        }
        return (<SettingsItemNavigate title={summaryTitle} titleNumberOfLines={0} description={disputeItem?.subtitle ?? selectedMeta?.topLabel} titleFirst={!!disputeItem?.subtitle} icon={selectedMeta?.icon} renderIcon={selectedMeta?.renderIcon} disabled={isSingleItem} isSelected={isPickerOpen} onPress={onPressPicker}/>);
    };
    const renderContent = () => {
        if (!disputeItem || hasError)
            return null;
        if (isFetching || !formFields) {
            return (<View style={styles.loader}>
              <ActivityIndicator />
            </View>);
        }
        return (<>
            {!isWeb && !isWebViewReady && (<View style={styles.loader}>
                <ActivityIndicator />
              </View>)}
            <DisputeWebView formFields={formFields} html={html} onError={onDisputeError} onReady={onWebViewReady}/>
          </>);
    };
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={[
            styles.scrollContent,
            contentContainerStyle,
        ]} showsVerticalScrollIndicator={false}>
            <ScreenInfoHeader title={strings.headerTitle} subTitle={strings.headerSubtitle}/>
            <SettingsSection name={strings.itemSectionName} noMarginTop>
              {renderSummary()}
            </SettingsSection>
            <Spacer.H16 />
            {disputeItem ? renderContent() : null}
          </Animated.ScrollView>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditReportDisputeScreen;
const styles = StyleSheet.create({
    scrollContent: {
        flexGrow: 1,
    },
    loader: {
        paddingHorizontal: rem(24),
        paddingVertical: rem(40),
    },
});
